import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ArchivePreview = ({ archiveData }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleZoomToggle = () => {
    setIsZoomed(!isZoomed);
  };

  const handleDownload = () => {
    // Create download link for the screenshot
    const link = document.createElement('a');
    link.href = archiveData?.screenshot?.url;
    link.download = `polynotary-${archiveData?.id}-screenshot.png`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const handleFullscreen = () => {
    window.open(archiveData?.screenshot?.url, '_blank');
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Camera" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Webpage Screenshot</h3>
            <p className="text-sm text-muted-foreground">
              Captured on {archiveData?.timestamp}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={handleDownload}
          >
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Maximize"
            onClick={handleFullscreen}
          >
          </Button>
        </div>
      </div>
      {/* Screenshot Preview */}
      <div className="relative bg-muted">
        {!imageLoaded && (
          <div className="flex items-center justify-center h-96 bg-muted">
            <div className="flex flex-col items-center space-y-3">
              <Icon name="Loader2" size={32} className="text-muted-foreground animate-spin" />
              <p className="text-sm text-muted-foreground">Loading screenshot...</p>
            </div>
          </div>
        )}
        
        <div 
          className={`relative overflow-hidden cursor-zoom-in ${
            isZoomed ? 'cursor-zoom-out' : ''
          } ${!imageLoaded ? 'hidden' : ''}`}
          onClick={handleZoomToggle}
        >
          <Image
            src={archiveData?.screenshot?.url}
            alt="Archived webpage screenshot"
            className={`w-full transition-transform duration-300 ${
              isZoomed ? 'scale-150 cursor-zoom-out' : 'scale-100'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Zoom Overlay */}
          <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded text-xs">
            {isZoomed ? 'Click to zoom out' : 'Click to zoom in'}
          </div>
        </div>

        {/* Mobile Touch Controls */}
        <div className="md:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2">
          <div className="flex items-center space-x-2 bg-black/70 text-white px-3 py-2 rounded-full">
            <Button
              variant="ghost"
              size="sm"
              iconName="ZoomOut"
              onClick={() => setIsZoomed(false)}
              className="text-white hover:bg-white/20"
            />
            <Button
              variant="ghost"
              size="sm"
              iconName="ZoomIn"
              onClick={() => setIsZoomed(true)}
              className="text-white hover:bg-white/20"
            />
          </div>
        </div>
      </div>
      {/* Image Info */}
      <div className="p-4 bg-muted/30">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Resolution</span>
            <p className="font-medium text-foreground">{archiveData?.screenshot?.resolution}</p>
          </div>
          <div>
            <span className="text-muted-foreground">File Size</span>
            <p className="font-medium text-foreground">{archiveData?.screenshot?.fileSize}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Format</span>
            <p className="font-medium text-foreground">{archiveData?.screenshot?.format}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Quality</span>
            <p className="font-medium text-foreground">{archiveData?.screenshot?.quality}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchivePreview;